import { createFileRoute } from "@tanstack/react-router";
import type { ClientLocationPlanHydrated } from "api-types/locationPlans/locationPlansResponses";
import generateBuilding from "building-generation/generation/generateBuilding";
import { buildingSummaryForPaletteThumbnail } from "building-geometry/metrics/palette/buildingSummaryForPaletteThumbnail";
import type { Building } from "building-types/building/buildingTypes";
import type { BuildingSummaryForPaletteThumbnail } from "building-types/building/showroomTypes";
import { WEB_API_BASE_PATH } from "common/constants";
import { getFileStorageBaseURL, getPostedFileURLByName } from "common/constants/fileStorageBaseUrl";
import { getPaletteThumbnailPath } from "common/palettes/paletteThumbnailCache";
import { proxifyURL } from "common/utils/fetchHelpers";
import TaskQueue from "common/utils/taskQueue";
import docParse from "doc/docMarshal";
import { isExteriorPaletteOptionSet } from "doc/options/paletteOptionSet";
import { compareImages } from "gfx/compareImages";
import THREEViewer from "gfx/THREEViewer";
import { activeTHREEViewerServices } from "gfx/THREEViewerServices";
import {
  generatePaletteThumbnailInternal,
  resolvePaletteThumbnailCacheId,
} from "lib/components/showroom2/options/generatePaletteThumbnail";
import type { MaterialThumbnailConfig } from "lib/house/paletteThumbnail";
import { postFile } from "lib/storage/fileStorage";
import { getHomePlanHydrated } from "lib/storage/homePlanStorage";
import type { StorageResponse } from "lib/storage/types";
import { getWithTimeout } from "lib/utils/fetchAuthHelpers";
import getPageTitle from "lib/utils/getPageTitle";
import type { ReactNode } from "react";
import { useState } from "react";
import { useEffect } from "react";
import { Helmet as Head } from "react-helmet-async";
import Input from "ui/forms/Input";
import { ConfigTable } from "ui/tables/DataTable";
import type { Column } from "ui/tables/DataTable/types";
import withAuthentication from "../../components/withAuthentication";

const planTypes = [
  { value: "home", label: "Home" },
  { value: "community", label: "Location" },
  { value: "master", label: "Master" },
] as const;

const getPlanTypeDisplayName = (type: string): string => {
  const planType = planTypes.find((p) => p.value === type);
  return planType ? planType.label : type;
};

const uploadImageToS3 = async (
  comparison: PaletteThumbnailComparison,
  builderId: string,
  layout: "square" | "rect",
): Promise<void> => {
  const layoutComparison = comparison.layouts.find((l) => l.layout === layout);
  if (!layoutComparison?.freshImageUrl || !builderId) {
    throw new Error(`No fresh image available for ${layout} layout or builder ID missing`);
  }

  // Convert data URL to blob
  const response = await fetch(layoutComparison.freshImageUrl);
  const blob = await response.blob();

  const thumbnailPath = getPaletteThumbnailPath(layoutComparison.cacheKey, builderId);

  // Upload the image to S3
  const [, err] = await postFile(blob, thumbnailPath, true);
  if (err) {
    throw err;
  }
};

// Get the location plan without the location id
export async function getLocationPlanByLocationPlanId(
  builderId: string,
  locationPlanId: string,
): StorageResponse<ClientLocationPlanHydrated> {
  const res = await getWithTimeout(
    `${WEB_API_BASE_PATH}/builders/${builderId}/location-plans?locationPlanId=${locationPlanId}`,
  );
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json);
  }
  return json;
}

interface LayoutComparison {
  layout: "square" | "rect";
  existingImageUrl: string | null;
  freshImageUrl: string | null;
  cacheKey: string;
  status: "changed" | "missing" | "no-change";
  imageDifference?: number; // Percentage difference (0-100)
  pixelDifferences?: number;
  totalPixels?: number;
  hash1?: string;
  hash2?: string;
  differenceImageUrl?: string | null;
}

interface PaletteThumbnailComparison {
  paletteId: string;
  paletteName: string;
  buildingSummary: BuildingSummaryForPaletteThumbnail;
  layouts: LayoutComparison[];
}

interface PlanInfo {
  id: string;
  name: string;
  type: "home" | "community" | "master";
}

interface PlanData {
  name: string;
  doc: any;
  optionChoices?: any;
}

async function loadPlanData(
  builderId: string,
  planId: string,
  planType: "home" | "community" | "master",
): Promise<PlanData> {
  if (planType === "home") {
    const [planData, error] = await getHomePlanHydrated(builderId, planId);
    if (error) {
      throw new Error(`Failed to fetch home plan: ${error.message}`);
    }
    if (!planData) {
      throw new Error("Home plan data not found");
    }
    return {
      name: planData.planName || `Home Plan ${planData.id}`,
      doc: planData.doc,
      optionChoices: planData.optionChoices || {},
    };
  } else if (planType === "community") {
    const [planData, error] = await getLocationPlanByLocationPlanId(builderId, planId);
    if (error) {
      throw new Error(`Failed to fetch community plan: ${error.message}`);
    }
    if (!planData) {
      throw new Error("Community plan data not found");
    }
    return {
      name: planData.name,
      doc: planData.doc,
      optionChoices: {},
    };
  } else {
    // Use the /public-plan endpoint to ensure parsedPlan is run for palette overrides
    const response = await fetch(
      `${WEB_API_BASE_PATH}/builders/${builderId}/public-plans?planType=master_plans&planId=${planId}`,
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch master plan: ${response.statusText}`);
    }
    const publicPlanData = await response.json();

    if (!publicPlanData || !publicPlanData.masterPlan || !publicPlanData.homePlan) {
      throw new Error("Master plan data not found");
    }

    return {
      name: publicPlanData.masterPlan.name || `Master Plan ${publicPlanData.masterPlan.id}`,
      doc: publicPlanData.homePlan.doc, // Use the parsed plan doc with palette overrides
      optionChoices: {},
    };
  }
}

/**
 * Check if an existing thumbnail image exists by trying multiple URLs
 */
async function checkExistingImageExists(existingImageUrl: string | null): Promise<boolean> {
  if (!existingImageUrl) {
    return false;
  }

  const proxifiedExistingUrl = proxifyURL(existingImageUrl);
  const urlsToTry = [existingImageUrl, proxifiedExistingUrl].filter((s) => typeof s === "string" && s !== null);

  for (const url of urlsToTry) {
    try {
      const res = await fetch(url, { method: "HEAD" });
      if (res.status === 200) {
        return true;
      }
    } catch (error) {
      // Silently continue to next URL
    }
  }

  return false;
}

/**
 * Generate a fresh thumbnail with timeout protection
 */
async function generateFreshThumbnail(
  paletteId: string,
  buildingSummary: any,
  buildingPalettes: any,
  layout: "square" | "rect",
  fileStorageBaseURL: string,
  materialConfig: MaterialThumbnailConfig = {},
): Promise<string | null> {
  try {
    const generationPromise = generatePaletteThumbnailInternal(
      paletteId,
      buildingSummary,
      buildingPalettes,
      {
        paletteLayout: layout,
      },
      fileStorageBaseURL,
      materialConfig,
    );

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Thumbnail generation timed out after 60 seconds for ${paletteId} (${layout})`));
      }, 60000); // 60 second timeout
    });

    const { dataURL } = await Promise.race([generationPromise, timeoutPromise]);
    return dataURL;
  } catch (err) {
    console.error(`Failed to generate fresh thumbnail for ${paletteId} (${layout}):`, err);
    return null;
  }
}

/**
 * Compare images and determine status with detailed logging
 */
async function compareImagesAndDetermineStatus(
  existingImageUrl: string | null,
  freshImageUrl: string | null,
  existingImageExists: boolean,
  paletteId: string,
  layout: "square" | "rect" = "square",
): Promise<{
  status: "changed" | "missing" | "no-change";
  imageDifference?: number;
  pixelDifferences?: number;
  totalPixels?: number;
  hash1?: string;
  hash2?: string;
  differenceImageUrl?: string | null;
}> {
  if (!existingImageExists) {
    return { status: "missing" };
  }

  if (!freshImageUrl) {
    return { status: "missing" };
  }

  // Both images exist, compare them using pixel-by-pixel analysis
  try {
    const comparisonResult = await compareImages(existingImageUrl as string, freshImageUrl);
    const { differencePercentage, pixelDifferences, totalPixels, hash1, hash2, differenceImageUrl } = comparisonResult;

    const status = differencePercentage > 1 ? "changed" : "no-change"; // 1% threshold for "no change"

    return {
      status,
      imageDifference: differencePercentage,
      pixelDifferences,
      totalPixels,
      hash1,
      hash2,
      differenceImageUrl,
    };
  } catch (error) {
    console.error(`Failed to compare images for ${paletteId} (${layout}):`, error);
    return { status: "changed" }; // Assume changed if comparison fails
  }
}

async function initializeTHREEViewerServices(): Promise<void> {
  if (activeTHREEViewerServices) {
    return;
  }

  try {
    new THREEViewer({
      container: null,
      options: {
        showAxes: false,
        gui: false,
        debugFeaturesEnabled: false,
        showShadowCamera: false,
        shadows: true,
        animate: false,
        ssfxRenderer: true,
      },
    });
  } catch (error) {
    console.error(`Failed to initialize THREE viewer services:`, error);
    throw new Error(
      `Failed to initialize THREE viewer services: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
}

async function generateBuildingFromPlan(planData: PlanData): Promise<Building> {
  try {
    const parsedDoc = docParse(planData.doc);

    return await generateBuilding({
      name: planData.name,
      doc: parsedDoc,
      optionChoices: planData.optionChoices,
      showPerformance: false,
      printWarningsToConsole: false,
    });
  } catch (genError) {
    throw new Error(`Failed to generate building: ${genError instanceof Error ? genError.message : "Unknown error"}`);
  }
}

function PaletteThumbnailCacheBust(): ReactNode {
  const [planId, setPlanId] = useState<string>("");
  const [planType, setPlanType] = useState<"home" | "community" | "master">("home");
  const [builderId, setBuilderId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [planInfo, setPlanInfo] = useState<PlanInfo | null>(null);
  const [comparisons, setComparisons] = useState<PaletteThumbnailComparison[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [currentProcessingPalettes, setCurrentProcessingPalettes] = useState<string[]>([]);
  const [replacingImages, setReplacingImages] = useState<Set<string>>(new Set());
  const [bulkReplacing, setBulkReplacing] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<{ completed: number; total: number }>({ completed: 0, total: 0 });
  const [activeTab, setActiveTab] = useState<"changed" | "missing" | "no-change">("changed");
  const [fileStorageBaseURL, setFileStorageBaseURL] = useState<string | null>(null);
  const [skipMaterialCache, setSkipMaterialCache] = useState<boolean>(false);

  useEffect(() => {
    void getFileStorageBaseURL().then((url) => {
      if (url) {
        setFileStorageBaseURL(url);
      }
    });
  }, []);

  const loadPlan = async (): Promise<void> => {
    if (!fileStorageBaseURL) {
      return;
    }

    if (!planId || !builderId) {
      setError("Please provide both Plan ID and Builder ID");
      return;
    }

    setLoading(true);
    setError(null);
    setPlanInfo(null);
    setComparisons([]);
    setCurrentProcessingPalettes([]);

    try {
      const planData = await loadPlanData(builderId, planId, planType);

      const building = await generateBuildingFromPlan(planData);

      setPlanInfo({
        id: planId,
        name: planData.name,
        type: planType,
      });

      const optionSets = building.doc.optionSets;
      const exteriorPaletteOptionSets = Object.values(optionSets).filter((optionSet) =>
        isExteriorPaletteOptionSet(optionSet),
      );

      const buildingSummary = buildingSummaryForPaletteThumbnail(building);

      const totalPalettes = exteriorPaletteOptionSets.reduce(
        (total, optionSet) => total + Object.keys(optionSet.options).length,
        0,
      );

      setProgress({ completed: 0, total: totalPalettes });

      await initializeTHREEViewerServices();

      const processPalette = async (paletteName: string, paletteId: string): Promise<PaletteThumbnailComparison> => {
        const paletteDisplayName = `${paletteName} (${paletteId})`;

        setCurrentProcessingPalettes((prev) => [...prev, paletteDisplayName]);

        try {
          const layouts: ("square" | "rect")[] = ["square", "rect"];
          const layoutComparisons: LayoutComparison[] = [];

          // Process each layout
          for (const layout of layouts) {
            const cacheKey = await resolvePaletteThumbnailCacheId(
              paletteId,
              building.doc.palettes,
              layout,
              buildingSummary.cacheKey,
            );
            const thumbnailPath = getPaletteThumbnailPath(cacheKey, builderId);

            // Get existing image URL and check if it exists
            const existingImageUrl = await getPostedFileURLByName(thumbnailPath);
            const existingImageExists = await checkExistingImageExists(existingImageUrl);

            // Generate fresh thumbnail for this layout
            const materialConfig: MaterialThumbnailConfig = skipMaterialCache ? { noCache: true } : {};
            const freshImageUrl = await generateFreshThumbnail(
              paletteId,
              buildingSummary,
              building.doc.palettes,
              layout,
              fileStorageBaseURL,
              materialConfig,
            );

            // Compare images and determine status
            const comparisonResult = await compareImagesAndDetermineStatus(
              existingImageUrl,
              freshImageUrl,
              existingImageExists,
              paletteId,
              layout,
            );

            const layoutComparison: LayoutComparison = {
              layout,
              existingImageUrl: existingImageExists ? existingImageUrl : null,
              freshImageUrl,
              cacheKey,
              ...comparisonResult,
            };

            layoutComparisons.push(layoutComparison);
          }

          const comparison: PaletteThumbnailComparison = {
            paletteId,
            paletteName,
            buildingSummary,
            layouts: layoutComparisons,
          };

          return comparison;
        } catch (error) {
          console.error(`Failed to process palette ${paletteId}:`, error);
          // Return a failed comparison with both layouts marked as missing
          return {
            paletteId,
            paletteName,
            buildingSummary,
            layouts: [
              {
                layout: "square",
                existingImageUrl: null,
                freshImageUrl: null,
                cacheKey: await resolvePaletteThumbnailCacheId(
                  paletteId,
                  building.doc.palettes,
                  "square",
                  buildingSummary.cacheKey,
                ),
                status: "missing",
              },
              {
                layout: "rect",
                existingImageUrl: null,
                freshImageUrl: null,
                cacheKey: await resolvePaletteThumbnailCacheId(
                  paletteId,
                  building.doc.palettes,
                  "rect",
                  buildingSummary.cacheKey,
                ),
                status: "missing",
              },
            ],
          };
        } finally {
          // Remove from currently processing list
          setCurrentProcessingPalettes((prev) => prev.filter((name) => name !== paletteDisplayName));
          // Update progress
          setProgress((prev) => ({ ...prev, completed: prev.completed + 1 }));
        }
      };

      // Process palettes in batches of 10 for better performance and resource management
      const taskQueue = new TaskQueue<PaletteThumbnailComparison>(10);
      const palettePromises: Promise<PaletteThumbnailComparison>[] = [];

      for (const optionSet of exteriorPaletteOptionSets) {
        for (const option of Object.values(optionSet.options)) {
          palettePromises.push(taskQueue.enqueue(async () => await processPalette(option.name, option.id)));
        }
      }

      // Wait for all comparisons to complete and collect results
      const allComparisons = await Promise.all(palettePromises);

      // Update state once with all comparisons to prevent flashing
      setComparisons(allComparisons);
      setCurrentProcessingPalettes([]);
    } catch (err) {
      setError(`Failed to load plan: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  const replaceSingleImage = async (
    comparison: PaletteThumbnailComparison,
    layout: "square" | "rect",
  ): Promise<void> => {
    const layoutComparison = comparison.layouts.find((l) => l.layout === layout);
    if (!layoutComparison?.freshImageUrl || !builderId) {
      setError(`No fresh image available for ${layout} layout or builder ID missing`);
      return;
    }

    const paletteId = comparison.paletteId;
    const replacingKey = `${paletteId}-${layout}`;
    setReplacingImages((prev) => new Set(prev).add(replacingKey));

    try {
      await uploadImageToS3(comparison, builderId, layout);

      // Update the comparison to reflect the replacement
      setComparisons((prevComparisons) =>
        prevComparisons.map((comp) =>
          comp.paletteId === paletteId
            ? {
                ...comp,
                layouts: comp.layouts.map((l) =>
                  l.layout === layout
                    ? {
                        ...l,
                        existingImageUrl: layoutComparison.freshImageUrl,
                        status: "no-change" as const,
                        imageDifference: 0,
                        pixelDifferences: 0,
                        totalPixels: 0,
                        differenceImageUrl: null,
                      }
                    : l,
                ),
              }
            : comp,
        ),
      );

      console.log(`Successfully replaced ${layout} image for palette ${paletteId}`);
    } catch (error) {
      console.error(`Failed to replace ${layout} image for palette ${paletteId}:`, error);
      setError(
        `Failed to replace ${layout} image for palette ${paletteId}: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    } finally {
      setReplacingImages((prev) => {
        const newSet = new Set(prev);
        newSet.delete(replacingKey);
        return newSet;
      });
    }
  };

  const replaceImage = async (comparison: PaletteThumbnailComparison, layout: "square" | "rect"): Promise<void> => {
    await replaceSingleImage(comparison, layout);
  };

  const replaceBothLayouts = async (comparison: PaletteThumbnailComparison): Promise<void> => {
    const layoutsToReplace = comparison.layouts.filter(
      (l) => l.freshImageUrl && (l.status === "changed" || l.status === "missing"),
    );

    if (layoutsToReplace.length === 0) {
      setError("No layouts available to replace");
      return;
    }

    // Replace all layouts in parallel
    const replacePromises = layoutsToReplace.map((layout) => replaceSingleImage(comparison, layout.layout));

    try {
      await Promise.all(replacePromises);
      console.log(`Successfully replaced both layouts for palette ${comparison.paletteId}`);
    } catch (error) {
      console.error(`Failed to replace layouts for palette ${comparison.paletteId}:`, error);
      setError(
        `Failed to replace layouts for palette ${comparison.paletteId}: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  };

  const bulkReplaceImages = async (tabType: "changed" | "missing" | "no-change"): Promise<void> => {
    if (!builderId) {
      setError("Builder ID is required for bulk replacement");
      return;
    }

    // Get all layout comparisons for the specified tab that can be replaced
    const layoutsToReplace: Array<{ comparison: PaletteThumbnailComparison; layout: "square" | "rect" }> = [];
    comparisons.forEach((comp) => {
      comp.layouts.forEach((layout) => {
        if (
          layout.status === tabType &&
          layout.freshImageUrl &&
          (layout.status === "changed" || layout.status === "missing")
        ) {
          layoutsToReplace.push({ comparison: comp, layout: layout.layout });
        }
      });
    });

    if (layoutsToReplace.length === 0) {
      setError(`No images available to replace in the ${tabType} tab`);
      return;
    }

    setBulkReplacing((prev) => new Set(prev).add(tabType));

    try {
      // Process all replacements in parallel using the shared upload function
      const replacePromises = layoutsToReplace.map(async ({ comparison, layout }) => {
        try {
          await uploadImageToS3(comparison, builderId, layout);
          return `${comparison.paletteId}-${layout}`;
        } catch (error) {
          console.error(`Failed to replace ${layout} image for palette ${comparison.paletteId}:`, error);
          throw error;
        }
      });

      // Wait for all replacements to complete
      const successfulReplacements = await Promise.allSettled(replacePromises);
      const successful = successfulReplacements.filter((result) => result.status === "fulfilled").length;
      const failed = successfulReplacements.filter((result) => result.status === "rejected").length;

      // Update all successful comparisons
      setComparisons((prevComparisons) =>
        prevComparisons.map((comp) => {
          const hasSuccessfulReplacement = layoutsToReplace.some(
            ({ comparison, layout }) =>
              comparison.paletteId === comp.paletteId &&
              successfulReplacements.some(
                (result) => result.status === "fulfilled" && result.value === `${comp.paletteId}-${layout}`,
              ),
          );

          if (hasSuccessfulReplacement) {
            return {
              ...comp,
              layouts: comp.layouts.map((l) => {
                const wasReplaced = layoutsToReplace.some(
                  ({ comparison, layout }) =>
                    comparison.paletteId === comp.paletteId &&
                    layout === l.layout &&
                    successfulReplacements.some(
                      (result) => result.status === "fulfilled" && result.value === `${comp.paletteId}-${layout}`,
                    ),
                );

                if (wasReplaced) {
                  return {
                    ...l,
                    existingImageUrl: l.freshImageUrl,
                    status: "no-change" as const,
                    imageDifference: 0,
                    pixelDifferences: 0,
                    totalPixels: 0,
                    differenceImageUrl: null,
                  };
                }
                return l;
              }),
            };
          }
          return comp;
        }),
      );

      console.log(`Bulk replacement completed: ${successful} successful, ${failed} failed`);

      if (failed > 0) {
        setError(`Bulk replacement completed with ${failed} failures. Check console for details.`);
      }
    } catch (error) {
      console.error(`Bulk replacement failed:`, error);
      setError(`Bulk replacement failed: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setBulkReplacing((prev) => {
        const newSet = new Set(prev);
        newSet.delete(tabType);
        return newSet;
      });
    }
  };

  const getColumnsForTab = (tabType: "changed" | "missing" | "no-change"): Column<PaletteThumbnailComparison>[] => {
    // Helper function to get image dimensions based on layout
    const getImageDimensions = (layout: "square" | "rect") => {
      if (layout === "rect") {
        // Rect layout: 500x300 (5:3 aspect ratio)
        return { width: 100, height: 60 }; // Maintains 5:3 ratio
      } else {
        // Square layout: 300x300 (1:1 aspect ratio)
        return { width: 80, height: 80 }; // Maintains 1:1 ratio
      }
    };

    const baseColumns: Column<PaletteThumbnailComparison>[] = [
      {
        header: "Palette ID",
        accessor: (row) => row.paletteId,
      },
      {
        header: "Palette Name",
        accessor: (row) => row.paletteName,
      },
      {
        header: "Layout",
        accessor: (row) => {
          const changedLayouts = row.layouts.filter((l) => l.status === tabType);
          if (changedLayouts.length === 0) {
            return "-";
          }
          return (
            <div>
              {changedLayouts.map((layout) => {
                const getStatusColor = (status: string) => {
                  if (status === "changed") {
                    return "#ff6b6b";
                  }
                  if (status === "missing") {
                    return "#ffa726";
                  }
                  return "#4caf50";
                };

                return (
                  <div
                    key={layout.layout}
                    style={{
                      color: getStatusColor(layout.status),
                      fontWeight: "bold",
                      fontSize: "12px",
                    }}
                  >
                    {layout.layout.toUpperCase()}
                  </div>
                );
              })}
            </div>
          );
        },
      },
      {
        header: "Difference",
        accessor: (row) => {
          const changedLayouts = row.layouts.filter((l) => l.status === tabType);
          if (changedLayouts.length === 0) {
            return "-";
          }
          return (
            <div>
              {changedLayouts.map((layout) => (
                <div key={layout.layout} style={{ marginBottom: "4px" }}>
                  {layout.imageDifference !== undefined ? (
                    <div>
                      <div style={{ fontWeight: "bold", fontSize: "12px" }}>{layout.imageDifference.toFixed(2)}%</div>
                      {layout.pixelDifferences !== undefined && layout.totalPixels !== undefined && (
                        <div style={{ fontSize: "10px", color: "#666" }}>
                          {layout.pixelDifferences.toLocaleString()} / {layout.totalPixels.toLocaleString()} pixels
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontSize: "12px" }}>-</div>
                  )}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        header: "Cache Key",
        accessor: (row) => {
          const changedLayouts = row.layouts.filter((l) => l.status === tabType);
          if (changedLayouts.length === 0) {
            return "-";
          }
          return (
            <div>
              {changedLayouts.map((layout) => (
                <div key={layout.layout} style={{ fontSize: "10px", marginBottom: "2px" }}>
                  {layout.layout}: {layout.cacheKey}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        header: "Existing Image",
        accessor: (row) => {
          const changedLayouts = row.layouts.filter((l) => l.status === tabType);
          if (changedLayouts.length === 0) {
            return "-";
          }
          return (
            <div>
              {changedLayouts.map((layout) => (
                <div key={layout.layout} style={{ marginBottom: "4px" }}>
                  <div style={{ fontSize: "10px", fontWeight: "bold" }}>{layout.layout}:</div>
                  {!layout.existingImageUrl ? (
                    <span style={{ color: "red", fontSize: "10px" }}>Not Found</span>
                  ) : (
                    <img
                      src={proxifyURL(layout.existingImageUrl)}
                      alt={`Existing ${row.paletteName} (${layout.layout})`}
                      style={{
                        ...getImageDimensions(layout.layout),
                        objectFit: "cover",
                      }}
                      crossOrigin="anonymous"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        header: "Fresh Image",
        accessor: (row) => {
          const changedLayouts = row.layouts.filter((l) => l.status === tabType);
          if (changedLayouts.length === 0) {
            return "-";
          }
          return (
            <div>
              {changedLayouts.map((layout) => (
                <div key={layout.layout} style={{ marginBottom: "4px" }}>
                  <div style={{ fontSize: "10px", fontWeight: "bold" }}>{layout.layout}:</div>
                  {!layout.freshImageUrl ? (
                    <span style={{ color: "red", fontSize: "10px" }}>Failed to Generate</span>
                  ) : (
                    <img
                      src={layout.freshImageUrl}
                      alt={`Fresh ${row.paletteName} (${layout.layout})`}
                      style={{
                        ...getImageDimensions(layout.layout),
                        objectFit: "cover",
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          );
        },
      },
    ];

    // Only show difference visualization for "changed" tab
    if (tabType === "changed") {
      baseColumns.push({
        header: "Difference Visualization",
        accessor: (row) => {
          const changedLayouts = row.layouts.filter((l) => l.status === tabType);
          if (changedLayouts.length === 0) {
            return "-";
          }
          return (
            <div>
              {changedLayouts.map((layout) => (
                <div key={layout.layout} style={{ marginBottom: "4px" }}>
                  <div style={{ fontSize: "10px", fontWeight: "bold" }}>{layout.layout}:</div>
                  {!layout.differenceImageUrl ? (
                    <span style={{ color: "#666", fontSize: "10px" }}>No differences</span>
                  ) : (
                    <div style={{ position: "relative" }}>
                      <img
                        src={layout.differenceImageUrl}
                        alt={`Differences for ${row.paletteName} (${layout.layout})`}
                        style={{
                          ...getImageDimensions(layout.layout),
                          objectFit: "cover",
                          backgroundColor: "#f0f0f0",
                          border: "1px solid #ddd",
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        },
      });
    }

    // Only show Actions column for tabs that need actions
    if (tabType === "changed" || tabType === "missing") {
      baseColumns.push({
        header: "Actions",
        accessor: (row) => {
          const changedLayouts = row.layouts.filter((l) => l.status === tabType);
          if (changedLayouts.length === 0) {
            return "-";
          }

          const canReplaceBoth =
            changedLayouts.length === 2 &&
            changedLayouts.every((l) => l.freshImageUrl && (l.status === "changed" || l.status === "missing"));
          const isReplacingBoth = changedLayouts.every((layout) =>
            replacingImages.has(`${row.paletteId}-${layout.layout}`),
          );

          return (
            <div>
              {/* Replace Both button - only show when both layouts need replacement */}
              {canReplaceBoth && (
                <div style={{ marginBottom: "8px" }}>
                  <button
                    onClick={() => replaceBothLayouts(row)}
                    disabled={isReplacingBoth}
                    style={{
                      padding: "6px 12px",
                      backgroundColor: isReplacingBoth ? "#ccc" : "#2196f3",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: isReplacingBoth ? "not-allowed" : "pointer",
                      fontSize: "11px",
                      fontWeight: "bold",
                      opacity: isReplacingBoth ? 0.6 : 1,
                    }}
                  >
                    {isReplacingBoth ? "Replacing Both..." : "Replace Both"}
                  </button>
                </div>
              )}

              {/* Individual layout buttons */}
              {changedLayouts.map((layout) => {
                const isReplacing = replacingImages.has(`${row.paletteId}-${layout.layout}`);
                const canReplace = layout.freshImageUrl && (layout.status === "changed" || layout.status === "missing");

                return (
                  <div key={layout.layout} style={{ marginBottom: "4px" }}>
                    <div style={{ fontSize: "10px", fontWeight: "bold", marginBottom: "2px" }}>{layout.layout}:</div>
                    <button
                      onClick={() => replaceImage(row, layout.layout)}
                      disabled={isReplacing || !canReplace}
                      style={{
                        padding: "4px 8px",
                        backgroundColor: canReplace ? "#4caf50" : "#ccc",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: canReplace ? "pointer" : "not-allowed",
                        fontSize: "10px",
                        opacity: isReplacing ? 0.6 : 1,
                      }}
                    >
                      {isReplacing ? "Replacing..." : "Replace"}
                    </button>
                  </div>
                );
              })}
            </div>
          );
        },
      });
    }

    return baseColumns;
  };

  return (
    <div>
      <Head>
        <title>{getPageTitle("Palette Thumbnail Cache Bust")}</title>
        <meta name="robots" content="noindex" />
      </Head>

      <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
        <h1>Palette Thumbnail Cache Bust Tool</h1>
        <p>
          This tool allows you to compare existing cached palette thumbnails with freshly generated ones for a specific
          plan. Enter a plan ID and builder ID to load the plan, generate the building using the full building
          generation pipeline, and create comparisons for all exterior palette option sets.
        </p>
        <div style={{ marginBottom: "20px" }}>
          <div style={{ marginBottom: "10px", display: "flex", gap: "20px", alignItems: "center" }}>
            <label>
              Plan Type:
              <select
                value={planType}
                onChange={(e) => setPlanType(e.target.value as "home" | "community" | "master")}
                style={{ marginLeft: "8px" }}
              >
                {planTypes.map((planTypeOption) => (
                  <option key={planTypeOption.value} value={planTypeOption.value}>
                    {planTypeOption.label} Plan
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="checkbox"
                checked={skipMaterialCache}
                onChange={(e) => setSkipMaterialCache(e.target.checked)}
              />
              Skip Material Cache
            </label>
          </div>

          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <div>
              <label>Builder ID:</label>
              <Input
                value={builderId}
                onChange={(e) => setBuilderId(e.target.value)}
                placeholder="Enter builder ID"
                style={{ width: "200px" }}
              />
            </div>
            <div>
              <label>Plan ID:</label>
              <Input
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                placeholder="Enter plan ID"
                style={{ width: "200px" }}
              />
            </div>
            <button onClick={loadPlan} disabled={loading || !planId || !builderId}>
              {loading ? "Loading..." : "Load Plan"}
            </button>
          </div>
        </div>

        {error && (
          <div style={{ color: "red", marginBottom: "20px", padding: "10px", backgroundColor: "#ffe6e6" }}>
            Error: {error}
          </div>
        )}

        {currentProcessingPalettes.length > 0 && (
          <div
            style={{ marginBottom: "20px", padding: "10px", backgroundColor: "#e6f3ff", border: "1px solid #4caf50" }}
          >
            <strong>🔄 Processing ({currentProcessingPalettes.length}):</strong>

            {/* Progress Bar */}
            {progress.total > 0 && (
              <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                  <span style={{ fontSize: "14px" }}>
                    Progress: {progress.completed} / {progress.total} palettes
                  </span>
                  <span style={{ fontSize: "14px", fontWeight: "bold" }}>
                    {Math.round((progress.completed / progress.total) * 100)}%
                  </span>
                </div>
                <div
                  style={{
                    width: "100%",
                    height: "20px",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "10px",
                    overflow: "hidden",
                    border: "1px solid #ccc",
                  }}
                >
                  <div
                    style={{
                      width: `${(progress.completed / progress.total) * 100}%`,
                      height: "100%",
                      backgroundColor: "#4caf50",
                      transition: "width 0.3s ease",
                      borderRadius: "10px",
                    }}
                  />
                </div>
              </div>
            )}

            <div style={{ marginTop: "5px", fontSize: "14px" }}>
              {currentProcessingPalettes.map((palette, index) => (
                <div key={index} style={{ marginBottom: "2px" }}>
                  • {palette}
                </div>
              ))}
            </div>
          </div>
        )}

        {planInfo && (
          <div style={{ marginBottom: "20px", padding: "8px 12px", backgroundColor: "#e6f3ff", borderRadius: "4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", fontSize: "14px" }}>
              <span style={{ fontWeight: "bold", color: "#1976d2" }}>✓ Loaded Plan:</span>
              <span>
                <strong>{getPlanTypeDisplayName(planInfo.type)}</strong> Plan
              </span>
              <span>•</span>
              <span>
                <strong>ID:</strong> {planInfo.id}
              </span>
              <span>•</span>
              <span>
                <strong>Name:</strong> {planInfo.name}
              </span>
            </div>
          </div>
        )}

        {comparisons.length > 0 && (
          <div>
            <h3>Exterior Palette Thumbnail Comparisons ({comparisons.length} total)</h3>

            {/* Clickable Summary Stats (serve as tabs) */}
            <div style={{ marginBottom: "20px" }}>
              {/* Navigation Tabs */}
              <div style={{ display: "flex", gap: "20px", marginBottom: "15px" }}>
                <div
                  onClick={() => setActiveTab("changed")}
                  style={{
                    padding: "10px",
                    backgroundColor: activeTab === "changed" ? "#ff6b6b" : "#ff6b6b",
                    color: "white",
                    borderRadius: "4px",
                    cursor: "pointer",
                    border: activeTab === "changed" ? "3px solid #fff" : "3px solid transparent",
                    transition: "border 0.2s ease",
                    opacity: activeTab === "changed" ? 1 : 0.8,
                  }}
                >
                  <strong>Changed:</strong>{" "}
                  {comparisons.filter((c) => c.layouts.some((l) => l.status === "changed")).length}
                </div>
                <div
                  onClick={() => setActiveTab("missing")}
                  style={{
                    padding: "10px",
                    backgroundColor: activeTab === "missing" ? "#ffa726" : "#ffa726",
                    color: "white",
                    borderRadius: "4px",
                    cursor: "pointer",
                    border: activeTab === "missing" ? "3px solid #fff" : "3px solid transparent",
                    transition: "border 0.2s ease",
                    opacity: activeTab === "missing" ? 1 : 0.8,
                  }}
                >
                  <strong>Missing:</strong>{" "}
                  {comparisons.filter((c) => c.layouts.some((l) => l.status === "missing")).length}
                </div>
                <div
                  onClick={() => setActiveTab("no-change")}
                  style={{
                    padding: "10px",
                    backgroundColor: activeTab === "no-change" ? "#4caf50" : "#4caf50",
                    color: "white",
                    borderRadius: "4px",
                    cursor: "pointer",
                    border: activeTab === "no-change" ? "3px solid #fff" : "3px solid transparent",
                    transition: "border 0.2s ease",
                    opacity: activeTab === "no-change" ? 1 : 0.8,
                  }}
                >
                  <strong>No Change:</strong>{" "}
                  {comparisons.filter((c) => c.layouts.some((l) => l.status === "no-change")).length}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                {activeTab === "changed" &&
                  comparisons.some((c) => c.layouts.some((l) => l.status === "changed" && l.freshImageUrl)) && (
                    <button
                      onClick={() => void bulkReplaceImages("changed")}
                      disabled={bulkReplacing.has("changed")}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: bulkReplacing.has("changed") ? "#ccc" : "#ff6b6b",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: bulkReplacing.has("changed") ? "not-allowed" : "pointer",
                        fontSize: "14px",
                        fontWeight: "bold",
                        opacity: bulkReplacing.has("changed") ? 0.6 : 1,
                      }}
                    >
                      {bulkReplacing.has("changed")
                        ? "Replacing..."
                        : `Replace All ${comparisons.reduce(
                            (count, c) => count + c.layouts.filter((l) => l.status === "changed").length,
                            0,
                          )} Changed`}
                    </button>
                  )}
                {activeTab === "missing" &&
                  comparisons.some((c) => c.layouts.some((l) => l.status === "missing" && l.freshImageUrl)) && (
                    <button
                      onClick={() => void bulkReplaceImages("missing")}
                      disabled={bulkReplacing.has("missing")}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: bulkReplacing.has("missing") ? "#ccc" : "#ffa726",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: bulkReplacing.has("missing") ? "not-allowed" : "pointer",
                        fontSize: "14px",
                        fontWeight: "bold",
                        opacity: bulkReplacing.has("missing") ? 0.6 : 1,
                      }}
                    >
                      {bulkReplacing.has("missing")
                        ? "Generating..."
                        : `Generate All ${comparisons.reduce(
                            (count, c) => count + c.layouts.filter((l) => l.status === "missing").length,
                            0,
                          )} Missing`}
                    </button>
                  )}
                {activeTab === "no-change" && (
                  <span style={{ color: "#4caf50", fontSize: "14px", fontStyle: "italic" }}>
                    No action needed - images are up to date
                  </span>
                )}
              </div>
            </div>

            {/* Table */}
            <ConfigTable
              columns={getColumnsForTab(activeTab)}
              data={comparisons
                .filter((c) => c.layouts.some((l) => l.status === activeTab))
                .sort((a, b) => {
                  // For "changed" tab, sort by highest image difference among layouts (highest first)
                  if (activeTab === "changed") {
                    const maxDiffA = Math.max(
                      ...a.layouts.filter((l) => l.status === activeTab).map((l) => l.imageDifference ?? 0),
                    );
                    const maxDiffB = Math.max(
                      ...b.layouts.filter((l) => l.status === activeTab).map((l) => l.imageDifference ?? 0),
                    );
                    return maxDiffB - maxDiffA; // Descending order (highest difference first)
                  }
                  // For other tabs, maintain original order
                  return 0;
                })}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const PaletteThumbnailCacheBustPage = (): ReactNode => <PaletteThumbnailCacheBust />;

export const Route = createFileRoute("/debug/palette-thumbnail-cache-bust")({
  component: withAuthentication(PaletteThumbnailCacheBustPage, { superAdmin: true }),
});
